<template>
  <Modal :title="translations.title" identifier="otherVendorsModal" size="lg" is-full-mobile>
    <div class="text-left">
      <div :class="{ 'mb-4': pickerRendersProperties() }">
        <VariantPicker
          :variant_properties="JSON.stringify(variantProperties)"
          :preselected-properties="[]"
          :identifier="pickerIdentifier"
          :translations="translations"
        />
      </div>
      <div class="text-left mb-4">
        <div class="row d-none d-md-flex">
          <div class="col-md-12 text-muted h6 mb-0">{{ translations.vendor }}</div>
          <div @click="setSorting" class="col-md-4 text-muted h6 mb-0 cursor-pointer">
            {{ translations.price }}
            <SortingArrows :show="!!sort" :asc="sort == 'asc'" />
          </div>
          <div class="col-md-4 text-muted h6 mb-0">{{ translations.shipping }}</div>
          <div class="col-md-4 text-muted h6 mb-0"></div>
        </div>
        <OtherVendorsModalVendorRow
          v-if="containsAllSelectedProperties(currentProduct.variants)"
          :product="currentProduct"
          :current="true"
          :translations="translations"
          :chosenVariants="chosenVariants"
          @click="setActiveMarker(currentProduct)"
          :class="{ 'selected-background': isActive(currentProduct) }"
        />
        <OtherVendorsModalVendorRow
          v-for="product in filteredProducts"
          :key="`vendor_${product.vendor_name}`"
          :product="product"
          :translations="translations"
          :chosenVariants="chosenVariants"
          @click="setActiveMarker(product)"
          :class="{ 'selected-background': isActive(product) }"
        />
      </div>
      <div class="mapSpacer">
        <GoogleMap :zoom="15" :center="currentProduct.vendor_location" @map-loaded="setMapLoaded">
          <div v-if="googleMap.mapLoaded">
            <GoogleMapMarker
              v-if="containsAllSelectedProperties(currentProduct.variants)"
              :icon="{
                url: isActive(currentProduct) ? MarkerIconActive : MarkerIcon,
                size: new googleMap.google.maps.Size(50, 60),
                scaledSize: new googleMap.google.maps.Size(50, 60),
              }"
              :google="googleMap.google"
              :map="googleMap.map"
              :position="currentProduct.vendor_location"
              :clickable="true"
              :title="currentProduct.vendor_name"
              @click="setActiveMarker(currentProduct)"
            />
            <GoogleMapMarker
              v-for="product in filteredProducts"
              :key="`otherVendorsMarker-${product.vendor_name}`"
              :icon="{
                url: isActive(product) ? MarkerIconActive : MarkerIcon,
                size: new googleMap.google.maps.Size(50, 60),
                scaledSize: new googleMap.google.maps.Size(50, 60),
              }"
              :google="googleMap.google"
              :map="googleMap.map"
              :position="product.vendor_location"
              :clickable="true"
              :title="product.vendor_name"
              @click="setActiveMarker(product)"
            />
          </div>
        </GoogleMap>
      </div>
    </div>
  </Modal>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import Modal from "./Modal";
import OtherVendorsModalVendorRow from "./OtherVendorsModalVendorRow";
import VariantPicker from "./VariantPicker";
import MarkerIcon from "@assets/images/static/marker/map-pin.png";
import MarkerIconActive from "@assets/images/static/marker/map-pin-active.png";
import GoogleMap from "./GoogleMap";
import GoogleMapMarker from "./GoogleMapMarker";
import ListSortingArrows from "./ListSortingArrows";
import compareProperties from "../mixins/compareProperties";
import storeTemplate from "../store/modules/variantPicker";

const pickerIdentifier = "OtherVendorsModal";

export default {
  name: "OtherVendorsModal",
  components: {
    Modal,
    OtherVendorsModalVendorRow,
    VariantPicker,
    GoogleMap,
    GoogleMapMarker,
    SortingArrows: ListSortingArrows,
  },
  mixins: [compareProperties],
  props: {
    currentProduct: {
      required: true,
      type: Object,
    },
    productsHash: {
      required: true,
      type: Array,
    },
    translations: {
      required: true,
      type: Object,
    },
    variantProperties: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      pickerIdentifier,
      MarkerIcon,
      MarkerIconActive,
      sort: null,
      active: this.currentProduct,
      googleMap: { map: null, google: null, mapLoaded: false },
    };
  },
  computed: {
    ...mapGetters({ productList: "otherVendors/getListProducts" }),
    selectedProperties() {
      return this.$store.getters[`variantPicker${pickerIdentifier}/selectedProperties`];
    },
    chosenVariants() {
      return this.$store.getters[`variantPicker${pickerIdentifier}/chosenVariants`];
    },
    filteredProducts() {
      return this.productList({ sort: this.sort }).filter(product => {
        return this.containsAllSelectedProperties(product.variants);
      });
    },
  },
  beforeCreate() {
    if (this.$store._modules.root._children[`variantPicker${pickerIdentifier}`]) return;
    this.$store.registerModule(`variantPicker${pickerIdentifier}`, storeTemplate);
  },
  mounted() {
    this.setListProducts(this.productsHash);
  },
  methods: {
    ...mapActions({ setListProducts: "otherVendors/setListProducts" }),
    pickerRendersProperties() {
      return this.variantProperties.some(variant => variant.properties.length > 0);
    },
    setSorting() {
      if (this.sort == "asc") {
        this.sort = "desc";
      } else {
        this.sort = "asc";
      }
    },
    setActiveMarker(product) {
      this.active = product;
      this.googleMap.map.panTo(product.vendor_location);
    },
    isActive(product) {
      return this.active == product;
    },
    setMapLoaded({ map, google }) {
      this.googleMap = { map, google, mapLoaded: true };
    },
    containsAllSelectedProperties(vendorVariants) {
      if (!this.selectedProperties) return true;
      return vendorVariants.some(vendorVariant =>
        this.selectedProperties.every(storeProperty =>
          vendorVariant.properties.some(vendorProperty =>
            this.compareProperties(storeProperty, vendorProperty)
          )
        )
      );
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~styles/global";

.mapSpacer {
  height: 350px;
}

.selected-background {
  background: $gray-100;
  border-radius: $border-radius;
}
</style>
